// libraries
import { Link } from "react-router-dom";
import { useAuth } from "../../utils/AuthContext";
import { useCallback } from "react";
import { UserRound, Settings, LogOut, HelpCircle, Menu } from "lucide-react";
import HelpCentreModal from "../../modals/HelpCentreModal";
import { useDisclosure } from "../../hooks/useDisclosure";
import { useClickOutside } from "../../hooks/useClickOutside";

interface UserNavbarProps {
  onMenuClick?: () => void;
}

function UserNavbar({ onMenuClick }: UserNavbarProps) {
  const { logout, user } = useAuth();
  const dropdown = useDisclosure();
  const helpModal = useDisclosure();

  const handleLogout = useCallback(async () => {
    try {
      await logout();
      window.location.href = "/login";
    } catch (error) {
      console.error("Logout failed:", error);
    }
  }, [logout]);

  const dropdownRef = useClickOutside<HTMLDivElement>(dropdown.close);

  const userName = user?.name || "User";
  const userEmail = user?.email || "";

  return (
    <>
      <nav className="!bg-[#1d365d]">
        <div className="w-full p-5 flex items-center justify-between relative">
          {/* Left section - hamburger on mobile, logo on desktop */}
          <div className="flex items-center">
            {onMenuClick && (
              <button
                type="button"
                onClick={onMenuClick}
                className="lg:hidden p-2 text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                aria-label="Open menu"
              >
                <Menu size={24} />
              </button>
            )}
            <Link to="/home/dashboard" id="logo-nav" className="hidden lg:block">
              <img
                src="/DataRevival_REV_RGB.svg"
                alt="PSDI Icon"
                className="w-[200px] h-auto"
              />
            </Link>
          </div>

          {/* Center section - logo on mobile only */}
          <Link to="/home/dashboard" className="lg:hidden absolute left-1/2 -translate-x-1/2">
            <img
              src="/DataRevival_REV_RGB.svg"
              alt="PSDI Icon"
              className="w-[150px] h-auto"
            />
          </Link>

          {/* dropdown */}
          <div className="relative" ref={dropdownRef}>
            <button
              type="button"
              className="flex text-sm rounded-full focus:ring-4 focus:ring-gray-600"
              onClick={dropdown.toggle}
              aria-expanded={dropdown.isOpen}
            >
              <span className="sr-only">Open user menu</span>
              <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center text-[#1d365d] font-semibold">
                {userName.charAt(0).toUpperCase()}
              </div>
            </button>
            {dropdown.isOpen && (
              <div className="absolute right-0 mt-2 z-50 w-44 rounded-lg overflow-hidden shadow-lg bg-white border border-gray-200">
                <div className="px-4 py-3 text-sm border-b border-gray-200 bg-white">
                  <span className="block text-gray-800 font-bold truncate">
                    {userName}
                  </span>
                  {userEmail && (
                    <span className="block text-gray-500 truncate text-sm">
                      {userEmail}
                    </span>
                  )}
                </div>
                <ul className="p-2 text-sm text-gray-700 font-medium bg-white">
                  <li>
                    <Link
                      to="/profile"
                      className="inline-flex items-center w-full p-2 hover:bg-gray-100 hover:text-gray-900 rounded transition-colors"
                    >
                      <UserRound size={16} className="mr-2" />
                      Profile
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/settings"
                      className="inline-flex items-center w-full p-2 hover:bg-gray-100 hover:text-gray-900 rounded transition-colors"
                    >
                      <Settings size={16} className="mr-2" />
                      Settings
                    </Link>
                  </li>
                  <li>
                    <button
                      onClick={helpModal.open}
                      className="inline-flex items-center w-full p-2 hover:bg-gray-100 hover:text-gray-900 rounded transition-colors mb-2"
                    >
                      <HelpCircle size={16} className="mr-2" />
                      Help centre
                    </button>
                  </li>
                  <hr />
                  <li>
                    <button
                      onClick={handleLogout}
                      className="inline-flex items-center w-full p-2 hover:bg-gray-100 hover:text-red-700 rounded text-left transition-colors text-red-600 mt-2"
                    >
                      <LogOut size={16} className="mr-2" />
                      Sign out
                    </button>
                  </li>
                </ul>
              </div>
            )}
          </div>
        </div>
      </nav>
      <HelpCentreModal isOpen={helpModal.isOpen} setIsOpen={helpModal.setIsOpen} />
    </>
  );
}

export default UserNavbar;
